import { useCallback, FunctionComponent, useRef } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";
import { Search } from "@ui-kit/Search/Search";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { Text } from "@ui-kit/Text/Text";
import { ExternalIcon } from "@icons/ExternalIcon";
import { getClientCoordinates } from "@lib/getClientCoordinates";

interface AddressAutocompleteItemProps {
  onPlaceSelect: (place: google.maps.places.Place | null) => void;
}

const AddressAutocompleteItem: FunctionComponent<
  AddressAutocompleteItemProps
> = ({ onPlaceSelect }) => {
  const placesLib = useMapsLibrary("places");
  const { t } = useTranslation();
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken>(null);
  const { latitude, longitude } = getClientCoordinates();

  const handleSuggestionClick = useCallback(
    async (suggestion: {
      data: google.maps.places.AutocompleteSuggestion;
      key: string;
      label: string;
      value: string;
    }) => {
      if (!suggestion.data.placePrediction) {
        return;
      }

      const place = suggestion.data.placePrediction.toPlace();
      await place.fetchFields({
        fields: ["location"],
      });

      sessionTokenRef.current = null;
      onPlaceSelect(place);
    },
    [onPlaceSelect],
  );

  const optionRender = useCallback(
    (option: {
      data: { data: google.maps.places.AutocompleteSuggestion };
      key: string;
      label: string;
      value: string;
    }) => {
      return (
        <div className="search-option-text">
          <Text variant="fontHeadingXsmall" color={colors.black}>
            {option.data.data.placePrediction?.mainText?.text}
          </Text>
          <Text variant="fontBodySmall" color={colors.neutral600}>
            {option.data.data.placePrediction?.secondaryText?.text}
          </Text>
        </div>
      );
    },
    [],
  );

  const handleSearchChange = useCallback(
    async (inputString: string) => {
      if (!placesLib) {
        return [];
      }

      const { AutocompleteSessionToken, AutocompleteSuggestion } = placesLib;

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new AutocompleteSessionToken();
      }

      const request: google.maps.places.AutocompleteRequest = {
        input: inputString,
        sessionToken: sessionTokenRef.current ?? undefined,
      };

      if (latitude && longitude) {
        request.locationBias = new google.maps.LatLng(latitude, longitude);
      }

      const response =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      return response.suggestions.map((suggestion) => ({
        key: suggestion.placePrediction?.placeId ?? "",
        label: suggestion.placePrediction?.text.text ?? "",
        value: suggestion.placePrediction?.placeId ?? "",
        data: suggestion,
      }));
    },
    [latitude, longitude, placesLib],
  );

  return (
    <div className="address-autocomplete-container">
      <Search
        autoFocus
        size="large"
        variant="default"
        prefix={
          <ExternalIcon
            svgName="search"
            size={24}
            color={colors.iconSecondary}
          />
        }
        width="100%"
        onSearchChange={handleSearchChange}
        optionRender={optionRender}
        onSelect={handleSuggestionClick}
        placeholder={t("screens.plan.searchForPlacePlaceholder")}
      />
    </div>
  );
};

export { AddressAutocompleteItem };
